"use client";
import { useEffect } from "react";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Lenis Smooth-Scroll + GSAP ScrollTrigger Integration.
 * Wird einmal in der Root-Layout-Page eingebunden.
 *
 * A11y/UX:
 *  - prefers-reduced-motion: Lenis bleibt komplett aus.
 *  - Skip-Link / Hash-Anchor: window.location.hash-Klicks werden mit
 *    immediate-Scroll bedient (kein Lenis-Tween), damit Tastatur-User
 *    instantan zum Ziel springen.
 *  - visibilitychange: bei verstecktem Tab Lenis stoppen und GSAP-Ticker
 *    schlafen legen — sonst laufen RAF-Callbacks im Hintergrund weiter.
 *  - Cleanup-Reihenfolge: erst Listener entfernen, dann destroy(), dann
 *    ScrollTrigger.kill(). Verhindert Race-Conditions bei Re-Navigation.
 */
export function LenisProvider() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduce) return;

    gsap.registerPlugin(ScrollTrigger);

    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),  // ease-out-expo
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });

    // Sync Lenis -> ScrollTrigger
    const onScroll = () => ScrollTrigger.update();
    lenis.on("scroll", onScroll);

    // Drive Lenis from GSAP ticker
    const tick = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    // Background-Tab-Pause: spart RAF/CPU, verhindert Memory-Drift.
    const onVisibility = () => {
      if (document.hidden) {
        lenis.stop();
        gsap.ticker.sleep();
      } else {
        lenis.start();
        gsap.ticker.wake();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Hash-Anchor & Skip-Link: instant scroll, kein Lenis-Tween.
    const onAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a[href^='#']") as HTMLAnchorElement | null;
      if (!anchor) return;
      const id = anchor.getAttribute("href");
      if (!id || id === "#") return;
      const el = document.querySelector(id);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el as HTMLElement, { immediate: true });
      // Fokus aufs Ziel setzen, damit Tastatur-Nav wirklich dort weitergeht.
      (el as HTMLElement).setAttribute("tabindex", "-1");
      (el as HTMLElement).focus({ preventScroll: true });
    };
    document.addEventListener("click", onAnchorClick);

    return () => {
      // Cleanup in defensiver Reihenfolge: erst Listener weg, dann destroy.
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("click", onAnchorClick);
      lenis.off("scroll", onScroll);
      gsap.ticker.remove(tick);
      lenis.destroy();
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  return null;
}
